import { resolveMetricParticipation } from "../platformAnalytics/metricBasis";

export const CANONICAL_CTR_LABEL = "Blended CTR";
/*
 * Eligibility-scoped since active-client-billed-v3-eligible-rates: the basis
 * is the click-eligible channels only. A channel whose source contract does
 * not measure clicks (video-first inventory such as CTV/YouTube) or that did
 * not report click evidence contributes NEITHER clicks NOR impressions —
 * its delivery still counts in total spend/impressions, just not in a rate
 * it cannot semantically join. A click-capable channel reporting a real zero
 * stays in the denominator. Never revert this to totalClicks /
 * totalImpressions across all channels. Eligibility comes from the
 * byte-pinned metricBasis engine so there is exactly ONE rule per repo.
 */
export const CANONICAL_CTR_DEFINITION =
  "clicks divided by impressions across click-eligible channels (channels whose governed source reports click evidence; video-first inventory is excluded)";

const toFiniteNumber = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "string" && !value.trim()) return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

export function deriveCtrPercentFromClicks({ clicks, impressions } = {}) {
  const clicksNum = toFiniteNumber(clicks);
  const impressionsNum = toFiniteNumber(impressions);
  if (clicksNum == null || impressionsNum == null || impressionsNum <= 0) return null;
  const pct = (clicksNum / impressionsNum) * 100;
  return Number.isFinite(pct) ? pct : null;
}

export function buildCtrTotalsFromChannelEntries(entries = []) {
  return (Array.isArray(entries) ? entries : []).reduce(
    (acc, entry) => {
      const metrics = entry?.metrics || entry?.raw || entry?.topline?.raw || entry || {};
      const participation = resolveMetricParticipation("ctr", {
        platformKey: entry?.key ?? null,
        metrics,
      });
      if (participation?.eligible) {
        acc.impressions += participation.denominator;
        acc.clicks += participation.numerator;
        if (entry?.key) acc.platformKeys.push(entry.key);
        if (entry?.label) acc.platformLabels.push(entry.label);
      } else if (entry?.key) {
        acc.excludedPlatformKeys.push(entry.key);
        if (entry?.label) acc.excludedPlatformLabels.push(entry.label);
      }
      return acc;
    },
    {
      impressions: 0,
      clicks: 0,
      platformKeys: [],
      platformLabels: [],
      excludedPlatformKeys: [],
      excludedPlatformLabels: [],
    }
  );
}

export function deriveCrossChannelCtrPercent(byChannel = {}) {
  const entries = Object.entries(byChannel || {}).map(([key, metrics]) => ({
    key,
    metrics,
  }));
  const totals = buildCtrTotalsFromChannelEntries(entries);
  if (!totals.platformKeys.length) return null;
  return deriveCtrPercentFromClicks(totals);
}
